import type React from 'react';

import type {
  FilterConfig,
  SetColumnFilter
} from '@/components/ui/table/filters/filters.type';
import type {
  TableColumnTransformed,
  TableConfigRaw
} from '@/components/ui/table/table.type';
import { getFilterConfig as getWidgetFilterConfig } from '@/components/ui/widget';
import {
  filterData as doFilterData,
  isFilterablePath
} from '@/utils/filtering/filtering.util';
import { DateRangeFilter } from './dateRange/dateRange.filter';
import { DropDownFilter } from './dropDown/dropDown.filter';
import { TextFilter } from './text/text.filter';

// client side table filter

// filters type to component map
export const filtersMap = {
  text: TextFilter,
  dateRange: DateRangeFilter,
  select: DropDownFilter
  // biome-ignore lint/suspicious/noExplicitAny: we don't care about the exact props here
} satisfies Record<FilterConfig['type'], React.NamedExoticComponent<any>>;

// get the relevant filter config to apply
// ie either specified in the column definition or default provide by the cell widget itself
const getFilterConfig = (colConfig: TableColumnTransformed): FilterConfig =>
  colConfig.def?.filter || getWidgetFilterConfig(colConfig.def?.cell || {});

// get the relevant filter
const getFilterComponent = (filterConfig: FilterConfig) =>
  filtersMap[filterConfig.type] || TextFilter; // default to simple text input filter

/** Renders the filter component. */
export const renderFilterComponent = (
  colConfig: TableColumnTransformed,
  setColumnFilter: SetColumnFilter,
  serverSideFiltering = false
) => {
  const filterConfig = getFilterConfig(colConfig);
  if (serverSideFiltering && !isFilterablePath(filterConfig.path)) {
    return null;
  }
  const FilterComponent = getFilterComponent(filterConfig);
  return (
    <FilterComponent
      colConfig={colConfig as any}
      filterConfig={filterConfig as never} // gave up on typing here
      setColumnFilter={setColumnFilter as any}
    />
  );
};

const applyColumnFilter = (
  dataToDisplay: unknown[],
  colConfig: TableColumnTransformed
) => {
  if (colConfig.filter) {
    // note: we let the filter function do the data.filter() itself for performance reasons as some preprocessing might be required in specific cases
    const filterConfig = getFilterConfig(colConfig);
    return doFilterData(dataToDisplay, filterConfig, colConfig.filter.value);
  }
  return dataToDisplay;
};

export const filterData = (
  dataArray: unknown[],
  displayConfig: TableConfigRaw
): unknown[] => {
  // apply successive fixed column filters
  const dataToDisplay =
    displayConfig.fixedColumns?.reduce(applyColumnFilter, dataArray) || [];
  // apply successive column filters
  return displayConfig.columns?.reduce(applyColumnFilter, dataToDisplay);
};
